import { ExternalLink, Maximize2, Pin, PinOff, RotateCw, ScanLine, ZoomIn, ZoomOut } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ZOOMS = [0.5, 0.75, 1, 1.5, 2, 3, 4];

/** Object URL for any File (images and PDFs), revoked when the file changes. */
function useFileUrl(file: Blob | null): string | null {
  const url = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
}

/** The image turned a quarter clockwise, as a new Blob (rotating with CSS breaks scrolling). */
async function rotateQuarter(src: string): Promise<Blob | null> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalHeight;
  canvas.height = img.naturalWidth;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.translate(canvas.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, 0, 0);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
}

/**
 * The passport scan the reader couldn't handle, shown large so the details
 * can be copied from it: zoom, rotate, drag to pan, and pin it in view while
 * typing in the form below. A PDF opens in the browser's own viewer.
 */
export function ScanViewer({ file, className }: { file: File; className?: string }) {
  const isPdf = file.type === 'application/pdf';
  const [rotated, setRotated] = React.useState<Blob | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [pinned, setPinned] = React.useState(false);
  const fileUrl = useFileUrl(file);
  const rotatedUrl = useFileUrl(rotated);
  const src = rotatedUrl ?? fileUrl;
  const box = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  React.useEffect(() => {
    setRotated(null);
    setZoom(1);
  }, [file]);

  const step = (dir: 1 | -1) => {
    const i = ZOOMS.indexOf(zoom);
    setZoom(ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, i + dir))] ?? 1);
  };
  const rotate = async () => {
    if (!src) return;
    const blob = await rotateQuarter(src);
    if (blob) setRotated(blob);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const el = box.current;
    if (!el || zoom <= 1) return;
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const el = box.current;
    const d = drag.current;
    if (!el || !d) return;
    el.scrollLeft = d.left - (e.clientX - d.x);
    el.scrollTop = d.top - (e.clientY - d.y);
  };
  const endDrag = () => {
    drag.current = null;
  };

  return (
    <section
      aria-label="Passport scan"
      className={cn(
        'overflow-hidden rounded-2xl border bg-card shadow-sm',
        // Pinned: stays under the app header while the form scrolls past
        pinned && 'sticky top-16 z-10 shadow-xl',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
        <ScanLine className="size-4 text-primary-ink" />
        <div className="mr-auto min-w-0">
          <p className="text-sm font-semibold">Your passport scan</p>
          {!pinned && <p className="text-xs text-muted-foreground">We couldn't read it automatically. Copy the details from it into the form below.</p>}
        </div>
        {!isPdf && (
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" className="size-8" onClick={() => step(-1)} disabled={zoom === ZOOMS[0]} title="Zoom out">
              <ZoomOut />
            </Button>
            <button type="button" className="w-12 text-center text-xs font-semibold tabular-nums text-muted-foreground hover:text-foreground" onClick={() => setZoom(1)} title="Fit to width">
              {zoom === 1 ? 'Fit' : `${Math.round(zoom * 100)}%`}
            </button>
            <Button type="button" variant="ghost" size="icon" className="size-8" onClick={() => step(1)} disabled={zoom === ZOOMS[ZOOMS.length - 1]} title="Zoom in">
              <ZoomIn />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="size-8" onClick={rotate} title="Rotate a quarter turn">
              <RotateCw />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="size-8" onClick={() => setZoom(1)} title="Fit to width">
              <Maximize2 />
            </Button>
          </div>
        )}
        <Button type="button" variant={pinned ? 'secondary' : 'outline'} size="sm" onClick={() => setPinned((p) => !p)} title="Keep the scan in view while you type">
          {pinned ? <PinOff /> : <Pin />} {pinned ? 'Unpin' : 'Keep in view'}
        </Button>
        {fileUrl && (
          <Button asChild variant="ghost" size="icon" className="size-8" title="Open in a new tab">
            <a href={fileUrl} target="_blank" rel="noreferrer">
              <ExternalLink />
            </a>
          </Button>
        )}
      </div>

      {isPdf ? (
        fileUrl && (
          <object data={fileUrl} type="application/pdf" className={cn('block w-full bg-secondary/40', pinned ? 'h-[34vh]' : 'h-[65vh]')}>
            <p className="p-6 text-sm text-muted-foreground">
              This browser can't show the PDF here.{' '}
              <a href={fileUrl} target="_blank" rel="noreferrer" className="font-semibold underline">
                Open it in a new tab
              </a>
              .
            </p>
          </object>
        )
      ) : (
        <div
          ref={box}
          className={cn(
            'overflow-auto bg-[repeating-conic-gradient(hsl(var(--secondary))_0_25%,transparent_0_50%)] bg-[length:20px_20px] transition-[height]',
            pinned ? 'h-[34vh]' : 'h-[min(65vh,640px)]',
            zoom > 1 && 'cursor-grab active:cursor-grabbing',
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {src && (
            <img
              src={src}
              alt="Passport scan"
              draggable={false}
              className={cn('mx-auto block max-w-none select-none', zoom === 1 && 'max-h-full w-auto max-w-full object-contain')}
              style={zoom === 1 ? undefined : { width: `${zoom * 100}%` }}
            />
          )}
        </div>
      )}
    </section>
  );
}
